import React from 'react'

import { Text, View, Link } from '@aws-amplify/ui-react'
import { choose, shuffle } from '@mytmpvpn/mytmpvpn-common/utils'

const PROVISIONING_FIRST_TIPS = [
  "Launching a fresh server with new keys and config — nothing reused, nothing shared...",
  "Building your server from scratch: instance, keys, WireGuard config — all unique to you...",
  "Why the wait? We provision a whole new server, generate keys and config on the spot...",
  "No pre-built pool: dedicated server + fresh keys + new WireGuard config, just for you...",
  "Spinning up server, generating keys, configuring WireGuard — all created now, never before...",
]

const PROVISIONING_TIPS = [
  { text: "Free peanuts? Refer a friend", link: "/referral" },
  { text: "Read our docs", link: "/doc" },
  { text: "Got an issue? Contact support", link: "/support" },
  { text: "Top up your peanuts", link: "/pricing" },
  { text: "Your server is yours alone, never shared", link: undefined },
  { text: "Use the CLI for scripted workflows", link: "/doc/cli" },
  { text: "Set a budget with maxPeanuts", link: "/#configuration" },
  { text: "Set a max duration — VPN auto-deletes when time is up", link: "/#configuration" },
  { text: "IPv6 supported in most locations", link: "/#locations" },
  { text: "No-subcription, pay only for what you use", link: "/pricing" },
  { text: "Share your referral code, earn peanuts", link: "/referral" },
  { text: "Available in 50+ locations worldwide", link: "/#locations" },
  { text: "Your config file works on any WireGuard client", link: "/doc" },
  { text: "Running low on peanuts? Grab a pack", link: "/pricing" },
  { text: "Want to help? We welcome contributors", link: "/contribute" },
  { text: "Have improvement ideas? Share them with us", link: "/issue" },
  { text: "Help translate MyTmpVpn to your language", link: "/contribute" },
  { text: "Got feedback? Open an issue", link: "/issue" },
  { text: "No subscription — buy peanuts once, use anytime", link: "/pricing" },
  { text: "No SSH on your server — no one can access it remotely", link: "/doc" },
  { text: "No cookies, no tracking, no marketing emails", link: "/doc" },
  { text: "Check our FAQ for common questions", link: "/faq" },
  { text: "Connect from mobile: scan the QR code in WireGuard app", link: "/faq" },
  { text: "VPN deleted? Just create a new one instantly", link: "/#locations" },
  { text: "Run multiple VPNs in different locations at once", link: "/#locations" },
  { text: "Power user? Try the CLI: npx @mytmpvpn/mytmpvpn-cli", link: "/doc/cli" },
  { text: "There is also an SDK for programmatic access", link: "/doc" },
  { text: "Choose a DNS resolver: Cloudflare, Google, or random", link: "/#configuration" },
  { text: "Network restrictions? Pick a custom port like 443 or 53", link: "/#configuration" },
  { text: "Short on peanuts? Use a different profile", link: "/#configuration" },
  { text: "Streaming 4K? Pick the Fast profile for ideal bandwidth", link: "/#configuration" },
  { text: "Just browsing? The Eco profile saves peanuts", link: "/#configuration" },
  { text: "Traveling with a group? Ultra shares one VPN with up to 10 people", link: "/#configuration" },
  { text: "Higher profile = more bandwidth but more peanuts/min", link: "/#configuration" },
  { text: "Don't waste peanuts on idle VPNs — set a max duration", link: "/#configuration" },
  { text: "Forgot to delete? Max duration auto-cleans up for you", link: "/#configuration" },
  { text: "Going to sleep? Set a short duration so it self-destructs", link: "/#configuration" },
  { text: "Fully open source — inspect the code on GitLab", link: "/contribute" },
  { text: "Quad9 or OpenDNS also available as DNS resolvers", link: "/#configuration" },
  { text: "Unused peanuts never expire — use them whenever you want", link: "/pricing" },
  { text: "WireGuard: modern, fast, open-source encryption", link: "/faq" },
  { text: "Each VPN runs on a dedicated server", link: "/faq" },
  { text: "Temporary by design: VPNs are meant to be destroyed", link: "/doc" },
  { text: "Available on 6 continents — from São Paulo to Tokyo", link: "/#locations" },
  { text: "CLI supports --sync to wait for VPN readiness", link: "/doc/cli" },
  { text: "Trust no one? Run your own MyTmpVpn instance", link: "/makemoney" },
  { text: "Both referrer and referee earn bonus peanuts", link: "/referral" },
  { text: "Your VPN stops automatically if peanuts run out", link: "/#configuration" },
  { text: "No account lock-in — no recurring charges", link: "/pricing" },
  { text: "Multiple profiles support in the CLI for different accounts", link: "/doc/cli" },
]

const DEPROVISIONING_FIRST_TIPS = [
  "Tearing down server and wiping all keys — nothing left behind...",
  "Destroying instance and keys — no trace remains...",
  "Shutting down and erasing everything: server, keys...",
  "Cleanup in progress — server and keys...",
]

const DEPROVISIONING_TIPS = [
  { text: "All keys destroyed, not just revoked", link: undefined },
  { text: "Server will be fully terminated, not recycled", link: undefined },
  { text: "No data retained after deletion — zero footprint", link: undefined },
  { text: "Spin up a new one anytime from any location", link: "/#locations" },
  { text: "Free peanuts? Refer a friend", link: "/referral" },
  { text: "Your config file is now useless — keys are gone", link: undefined },
  { text: "Infrastructure fully decommissioned, not just stopped", link: undefined },
  { text: "Need another VPN? Create one in a different location", link: "/#locations" },
  { text: "No recycling, no reuse — fresh instance every time", link: undefined },
]

interface Tip { text: string, link?: string }

function TipDisplay({ tip }: { tip: Tip }) {
  return <View>
    {tip.link
      ? <Link href={tip.link} fontSize={'smaller'} fontStyle={'italic'}>{tip.text}</Link>
      : <Text fontSize={'smaller'} fontStyle={'italic'}>{tip.text}</Text>
    }
  </View>
}

/**
 * Hook that returns a tip from a shuffled queue, advancing on each pollCount change.
 * The user never sees the same tip twice until the entire list has been shown.
 */
function useShuffledTips(tips: Tip[], firstTips: string[], pollCount: number): Tip {
  const queue = React.useRef<Tip[]>(shuffle(tips))
  const index = React.useRef(0)

  const [tip, setTip] = React.useState<Tip>(() => {
    if (pollCount <= 1) {
      return { text: choose(firstTips), link: undefined }
    }
    const t = queue.current[0]!
    index.current = 1
    return t
  })

  React.useEffect(() => {
    if (pollCount <= 1) {
      setTip({ text: choose(firstTips), link: undefined })
      return
    }
    // Advance through the shuffled queue
    if (index.current >= queue.current.length) {
      // All tips shown — reshuffle and restart
      queue.current = shuffle(tips)
      index.current = 0
    }
    setTip(queue.current[index.current]!)
    index.current++
  }, [pollCount]) // eslint-disable-line react-hooks/exhaustive-deps

  return tip
}

export function ProvisioningTips({ pollCount }: { pollCount: number }) {
  const tip = useShuffledTips(PROVISIONING_TIPS, PROVISIONING_FIRST_TIPS, pollCount)
  return <TipDisplay tip={tip} />
}

export function DeprovisioningTips({ pollCount }: { pollCount: number }) {
  const tip = useShuffledTips(DEPROVISIONING_TIPS, DEPROVISIONING_FIRST_TIPS, pollCount)
  return <TipDisplay tip={tip} />
}
